import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from './ui';

// ---------------------------------------------------------------------------
// Horizontal bars
// ---------------------------------------------------------------------------

const BAR_TONES = {
  brand: 'bg-brand-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
  green: 'bg-emerald-500',
  slate: 'bg-slate-400',
};

export interface BarListItem {
  key: string;
  label: ReactNode;
  value: number;
  /** Shown instead of the raw value, e.g. "4.2 h". */
  display?: ReactNode;
  secondary?: ReactNode;
  tone?: keyof typeof BAR_TONES;
  to?: string;
}

export function BarList({ items, emptyText = 'Nothing to show yet', className }: { items: BarListItem[]; emptyText?: string; className?: string }) {
  if (items.length === 0) return <p className="px-5 py-10 text-center text-sm text-slate-500">{emptyText}</p>;
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ul className={cn('space-y-3 p-5', className)}>
      {items.map((item) => (
        <li key={item.key}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-slate-700">
              {item.to ? (
                <Link to={item.to} className="hover:text-brand-700 hover:underline">
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </span>
            <span className="shrink-0 font-medium text-slate-900 tabular-nums">{item.display ?? item.value.toLocaleString('en-US')}</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className={cn('h-2 rounded-full', BAR_TONES[item.tone ?? 'brand'])}
              style={{ width: `${item.value > 0 ? Math.max(2, (item.value / max) * 100) : 0}%` }}
            />
          </div>
          {item.secondary && <p className="mt-0.5 text-xs text-slate-500">{item.secondary}</p>}
        </li>
      ))}
    </ul>
  );
}

export interface StackSegment {
  key: string;
  value: number;
  label: string;
  className: string;
}

export interface StackedBarItem {
  key: string;
  label: ReactNode;
  segments: StackSegment[];
  secondary?: ReactNode;
  to?: string;
}

/** Bars split into parts, e.g. one person's open tickets by priority. */
export function StackedBarList({ items, emptyText = 'Nothing to show yet' }: { items: StackedBarItem[]; emptyText?: string }) {
  if (items.length === 0) return <p className="px-5 py-10 text-center text-sm text-slate-500">{emptyText}</p>;
  const totals = items.map((item) => item.segments.reduce((sum, segment) => sum + segment.value, 0));
  const max = Math.max(1, ...totals);
  return (
    <ul className="space-y-3 p-5">
      {items.map((item, index) => (
        <li key={item.key}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate text-slate-700">
              {item.to ? (
                <Link to={item.to} className="hover:text-brand-700 hover:underline">
                  {item.label}
                </Link>
              ) : (
                item.label
              )}
            </span>
            <span className="shrink-0 font-medium text-slate-900 tabular-nums">{totals[index].toLocaleString('en-US')}</span>
          </div>
          <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-slate-100" style={{ width: `${Math.max(2, (totals[index] / max) * 100)}%` }}>
            {item.segments
              .filter((segment) => segment.value > 0)
              .map((segment) => (
                <div
                  key={segment.key}
                  className={segment.className}
                  style={{ width: `${(segment.value / totals[index]) * 100}%` }}
                  title={`${segment.label}: ${segment.value}`}
                />
              ))}
          </div>
          {item.secondary && <p className="mt-0.5 text-xs text-slate-500">{item.secondary}</p>}
        </li>
      ))}
    </ul>
  );
}

export function ChartLegend({ items }: { items: { label: string; className: string; line?: boolean }[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
      {items.map((item) => (
        <span key={item.label} className="inline-flex items-center gap-1.5">
          <span className={cn(item.line ? 'h-0.5 w-4' : 'size-2.5 rounded-sm', item.className)} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend: counts as bars, an average as a line
// ---------------------------------------------------------------------------

export interface TrendPoint {
  key: string;
  label: string;
  count: number;
  value: number | null;
}

function niceCeil(value: number) {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 2.5, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

export function TrendChart({
  points,
  countLabel,
  valueLabel,
  formatValue,
}: {
  points: TrendPoint[];
  countLabel: string;
  valueLabel: string;
  formatValue(value: number): string;
}) {
  const width = 680;
  const height = 210;
  const left = 52;
  const right = 36;
  const top = 10;
  const bottom = 24;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const valueMax = niceCeil(Math.max(0, ...points.map((p) => p.value ?? 0)));
  const countMax = niceCeil(Math.max(0, ...points.map((p) => p.count)));
  const slot = chartWidth / Math.max(1, points.length);
  const barWidth = Math.max(2, Math.min(28, slot * 0.6));
  const labelEvery = Math.max(1, Math.ceil(points.length / 7));
  const x = (index: number) => left + index * slot + slot / 2;
  const y = (value: number) => top + chartHeight * (1 - value / valueMax);

  // Break the line where a bucket has no data.
  const segments: { index: number; value: number }[][] = [];
  let current: { index: number; value: number }[] = [];
  points.forEach((point, index) => {
    if (point.value === null) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push({ index, value: point.value });
    }
  });
  if (current.length) segments.push(current);

  return (
    <figure className="space-y-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-52 w-full" role="img" aria-label={`${valueLabel} and ${countLabel}`}>
        {[0, 0.5, 1].map((fraction) => {
          const lineY = top + chartHeight * (1 - fraction);
          return (
            <g key={fraction}>
              <line x1={left} x2={width - right} y1={lineY} y2={lineY} className="stroke-slate-200" strokeDasharray={fraction === 0 ? undefined : '3 3'} />
              <text x={left - 6} y={lineY + 3} textAnchor="end" className="fill-slate-400 text-[10px]">
                {formatValue(valueMax * fraction)}
              </text>
              <text x={width - right + 6} y={lineY + 3} textAnchor="start" className="fill-slate-300 text-[10px]">
                {Math.round(countMax * fraction)}
              </text>
            </g>
          );
        })}
        {points.map((point, index) => {
          const barHeight = point.count > 0 ? Math.max(2, (point.count / countMax) * chartHeight) : 0;
          return (
            <g key={point.key}>
              <rect x={left + index * slot} y={top} width={slot} height={chartHeight} className="fill-transparent">
                <title>{`${point.label}: ${point.count} ${countLabel.toLowerCase()}${point.value !== null ? `, ${valueLabel.toLowerCase()} ${formatValue(point.value)}` : ''}`}</title>
              </rect>
              {barHeight > 0 && (
                <rect x={x(index) - barWidth / 2} y={top + chartHeight - barHeight} width={barWidth} height={barHeight} rx={1.5} className="pointer-events-none fill-brand-100" />
              )}
              {(index % labelEvery === 0 || index === points.length - 1) && (
                <text x={x(index)} y={height - 6} textAnchor="middle" className="fill-slate-400 text-[10px]">
                  {point.label}
                </text>
              )}
            </g>
          );
        })}
        {segments.map((segment) => (
          <g key={segment[0].index} className="pointer-events-none">
            {segment.length > 1 && (
              <polyline
                points={segment.map((p) => `${x(p.index)},${y(p.value)}`).join(' ')}
                fill="none"
                className="stroke-brand-600"
                strokeWidth={2}
                strokeLinejoin="round"
              />
            )}
            {segment.map((p) => (
              <circle key={p.index} cx={x(p.index)} cy={y(p.value)} r={2.5} className="fill-brand-600" />
            ))}
          </g>
        ))}
      </svg>
      <ChartLegend
        items={[
          ...(segments.length > 0 ? [{ label: valueLabel, className: 'bg-brand-600', line: true }] : []),
          { label: countLabel, className: 'bg-brand-100' },
        ]}
      />
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Heatmap: one cell per day
// ---------------------------------------------------------------------------

export interface HeatmapCell {
  date: string;
  succeeded: number;
  skipped: number;
  failed: number;
}

const dayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
export const formatHeatmapDay = (date: string) => dayFormat.format(new Date(`${date}T12:00:00Z`));

export function heatmapCellClass(cell: HeatmapCell) {
  const total = cell.succeeded + cell.skipped + cell.failed;
  if (total === 0) return 'bg-slate-100';
  if (cell.failed > 0) return cell.succeeded + cell.skipped > 0 ? 'bg-amber-400' : 'bg-red-500';
  if (cell.succeeded >= 5) return 'bg-emerald-600';
  if (cell.succeeded >= 2) return 'bg-emerald-500';
  if (cell.succeeded === 1) return 'bg-emerald-400';
  return 'bg-emerald-200';
}

export function HeatmapRow({ cells }: { cells: HeatmapCell[] }) {
  return (
    <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
      {cells.map((cell) => {
        const parts = [
          cell.succeeded && `${cell.succeeded} did something`,
          cell.skipped && `${cell.skipped} had nothing to do`,
          cell.failed && `${cell.failed} failed`,
        ].filter(Boolean);
        return (
          <div
            key={cell.date}
            className={cn('h-5 min-w-1.5 rounded-sm', heatmapCellClass(cell))}
            title={`${formatHeatmapDay(cell.date)}: ${parts.length ? parts.join(', ') : 'didn’t run'}`}
          />
        );
      })}
    </div>
  );
}

export function HeatmapLegend() {
  return (
    <ChartLegend
      items={[
        { label: 'Did something', className: 'bg-emerald-500' },
        { label: 'Ran, nothing to do', className: 'bg-emerald-200' },
        { label: 'Some runs failed', className: 'bg-amber-400' },
        { label: 'Failed', className: 'bg-red-500' },
        { label: 'Didn’t run', className: 'bg-slate-100 ring-1 ring-slate-200' },
      ]}
    />
  );
}
